<template>
  <vue-draggable-resizable
    :w="width"
    :h="height"
    :x="posX"
    :y="posY"
    :min-height="300"
    :min-width="minWidth"
    :max-width="1400"
    @dragstop="onDragStop"
    @resizing="onResizing"
    :active="true"
    :parent="true"
    :resizable="true"
    class-name-handle="object-form-resizable-handle"
    :drag-handle="'.drag'"
    data-app
  >
    <v-layout
      ref="objectForm"
      class="home-form-container elevation-2"
      :style="{
        height: wasResized ? '100%' : 'fit-content',
        maxHeight:
          tabs[activeTab] && tabs[activeTab].label === 'Photos'
            ? '100%'
            : 'fit-content',
        height:
          tabs[activeTab] && tabs[activeTab].code === 'activities'
            ? '100%'
            : 'fit-content'
      }"
      column
    >
      <form-header
        :title="pageTitle"
        @close="tryCloseForm"
      >
        <form-options-menu
          :action-type="actionType"
          :object-permission="permissionName"
          :current-tab="tabs[activeTab]"
          @changeViewModeOfObject="changeViewModeOfObject"
          @openDetails="$emit('openDetails')"
          @refresh="$emit('refresh')"
          @clearData="$emit('clearData')"
        />
      </form-header>

      <v-tabs
        v-if="!loadingStructure"
        :value="activeTab"
        @change="changeTab"
        background-color="transparent"
        show-arrows
        centered
        height="40px"
        :key="openTabs + width"
        slider-color="accent"
      >
        <v-tab
          v-for="(tab, index) in tabs"
          :disabled="!tab.enabled || !hasPermissionToSeeTab(tab)"
          :key="tab.code"
          :ref="'#' + index"
          style="text-transform: none; font-size: 12px"
        >
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <div v-if="loadingStructure" style="position: relative; height: 200px">
        <loading-spinner
          :active.sync="loadingStructure"
          :is-full-page="false"
          color="accent"
          background-color="transparent"
        />
      </div>

      <div
        v-else
        ref="tabContainer"
        class="home-form-tabs-container"
        :style="{
          maxHeight:
            tabs[activeTab] && tabs[activeTab].label === 'Photos'
              ? '80%'
              : '65vh',
          maxHeight:
            tabs[activeTab] && tabs[activeTab].code === 'activities'
              ? '80%'
              : '65vh'
        }"
      >
        <slot name="formFields"/>

        <object-form-activities-tab
          v-if="tabs[activeTab] && tabs[activeTab].code === 'activities'"
          :object-uuid="objectUuid"
          :object-layer="objectLayer"
          :action-type="actionType"
          :object-data="objectData"
          :width="width"
        />
      </div>

      <v-flex grow />

      <v-flex shrink v-if="!loadingStructure">
        <v-layout
          row
          align-center
          justify-space-between
          style="width: 100%; padding: 0 20px"
        >
        <form-required />
          <v-layout row :justify-space-between="!!$slots.footerButtons" :justify-end="!$slots.footerButtons" v-if="!loadingStructure">
            <slot name="footerButtons" />

            <div>
              <v-btn
                v-if="activeTab > 0"
                @click="$emit('updateActiveTab', activeTab - 1)"
                text
                color="var(&#45;&#45;secondary-darker)"
                class="footer-button  mr-2"
              >
                {{ $t("Previous") }}
              </v-btn>

              <v-btn
                v-if="activeTab < numberOfTabs - 1"
                @click="$emit('updateActiveTab', activeTab + 1)"
                color="accent"
                class="footer-button mr-3"
              >
                {{ $t("Next") }}
              </v-btn>

              <v-tooltip top :disabled="fieldsStillNeedFilled.length === 0">
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-if="actionType !== 'view'"
                    @click="$emit('save')"
                    color="accent"
                    class="footer-button"
                    :loading="loadingSave"
                    v-on="on"
                    :class="{
                      disabled: isSaveBtNdisabled
                    }"
                    id="save-button"
                    :disabled="isSaveBtNdisabled"
                  >
                    {{ $t("Save") }}
                  </v-btn>
                </template>

                <div style="text-align: left">
                  <span>{{ $t("MissingFields") }}:</span>

                  <div v-for="field of fieldsStillNeedFilled" :key="field">
                    <span style="text-transform: capitalize; font-size: 12px">
                      {{ field }}
                    </span>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </v-layout>
        </v-layout>
      </v-flex>
    </v-layout>

    <slot name="dialogBox" />

    <app-dialog-box
      :open="closeDialogIsOpen"
      :title="$t('ClosingForm')"
      :content="
        $t('UnsavedChanges') +
          '\n' +
          $t('CheckCloseForm') +
          '\n' +
          $t('ChangesWillBeLost')
      "
      :main-action-label="$t('No')"
      @mainAction="closeDialogIsOpen = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="closeForm"
    />
  </vue-draggable-resizable>
</template>

<script>
import { bus } from "@/main";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import FormOptionsMenu from "../_Atoms/FormOptionsMenu";
import WindowHeaderButton from "../../../_Atoms/WindowHeaderButton";
import FormRequired from "../_Atoms/FormRequired";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import ObjectFormActivitiesTab from "@/components/Home/Forms/_SpecialTabs/Activities/ActivitiesTab";

import LoadingSpinner from "vue-loading-overlay";
import FormHeader from "@/components/Home/Forms/_Molecules/FormHeader";

export default {
  name: "DynamicObjectFormStructure",

  components: {
    FormHeader,
    ObjectFormActivitiesTab,
    AppDialogBox,
    FormRequired,
    WindowHeaderButton,
    FormOptionsMenu,
    ClipLoader,
    LoadingSpinner
  },

  mixins: [checkPermissionsMixin],

  props: {
    objectUuid: String,

    objectLayer: String,

    /**
     * Determines if the structure is still being loaded.
     */
    loadingStructure: {
      type: Boolean,
      default: true
    },
    /**
     * Determines if the save is still being loaded.
     */
    loadingSave: {
      type: Boolean,
      default: false
    },
    /**
     * Name of the permission of the current object type
     */
    permissionName: {
      type: String
    },
    /**
     * Key to translation of the name of the current object type
     */
    objectTypeTranslationKey: {
      type: String
    },
    /**
     * List of the tabs of the current object type.
     */
    tabs: {
      type: Array
    },

    /**
     * Number of the currently active tab
     */
    activeTab: {
      type: Number,
      default: 0
    },
    /**
     * Type of action desired when opening the form.
     */
    actionType: {
      type: String,
      default: "edit"
    },
    /**
     * Map html element.
     */
    mapElement: {
      required: true
    },

    /**
     * If the Street View is open or not.
     */
    streetView: {
      type: Boolean,
      default: false
    },
    assets: {
      type: Array
    },
    internalReference:{
      type: String
    },
    previousPosX: {
      type: Number
    },
    previousPosY: {
      type: Number
    },

    dataWasChanged: {
      type: Boolean,
      default: false
    },

    saveIsDisabled: {
      type: Boolean,
      default: false
    },
    minWidth: {
      type: Number,
      default: 300
    },

    objectData: Object,

    formHeightUpdate: Number,

    /**
     * Fields that still need to be filled
     */
    fieldsStillNeedFilled: Array
  },

  data() {
    return {
      posY: undefined,
      posX: undefined,
      width: 765,
      height: 400,

      closeDialogIsOpen: false,
      wasResized: false
    };
  },

  computed: {
    numberOfTabs() {
      return this.tabs.length;
    },

    openTabs() {
      let openTabs = [];
      this.tabs.map(({ code }) => {
        if (code) openTabs.push(code);
      });
      return openTabs.join(",");
    },
    isSaveBtNdisabled(){
      let disableBtn;
      let disable = true;
      const labelTab = this.tabs[this.activeTab].label;
      if(this.actionType === "addNetworkElementsToReservation"){
        switch (labelTab){
          case 'Template':
            disable = true;
            break;
          case 'Assets':
            disable = true;
            break;
          case 'Network Elements':
            //check if table of selected assets has elements
            disable = this.assets.length <= 0;
            break;
          case 'Request':
            if(this.internalReference){
              disable = false;
            }else{
              disable = true;
            }
            break;
          default:
            break;
        };
        disableBtn = (this.loadingSave || this.saveIsDisabled || disable);
      }else{
        disableBtn = (this.loadingSave || this.saveIsDisabled);
      }
      return disableBtn;
    },
    pageTitle() {
      let title = this.$i18n.tc(this.objectTypeTranslationKey, 1);
      let objectIdentifier = null;
      if (this.actionType !== 'create' && this.actionType !== 'createAssetReservation' && !!this.objectData){
        const keys = Object.keys(this.objectData);
        if(keys.length > 0){
          let prefix = keys[0].split('_')[0];
          let objectLabel = prefix+'_label';
          let objectId = prefix+'_id';
          objectIdentifier = this.objectData[objectLabel] ? this.objectData[objectLabel] : this.objectData[objectId]
        }
      }
      if (objectIdentifier !== null) {
        title += ' - ' + objectIdentifier;
      }
      if(this.actionType === 'view' || this.actionType === 'splicing'){
        title += ' - View Mode';
      }
      return title;
    },
  },


  watch: {
    streetView() {
      this.setInitialPosition();
    },

    loadingStructure(loading) {
      if (!loading) {
        this.$nextTick(() => {
          this.setInitialPosition();
        });
      }
    },

    activeTab() {
      this.wasResized = false;
    },

    formHeightUpdate() {
      this.updateContainerDimensions();
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.setInitialPosition();
    });
  },

  updated() {
    this.updateContainerDimensions();
    this.$nextTick(() => {
      this.disableOverflowIfSmall();
    });
  },

  methods: {
    /**
     * Defines the initial position of the current component.
     */
    setInitialPosition() {
      if (this.previousPosX) {
        if (this.mapElement.offsetWidth - this.width > this.previousPosX) {
          this.posX = this.previousPosX;
        } else {
          this.posX = 20;
        }
      } else {
        this.posX = Math.round((this.mapElement.offsetWidth - this.width) / 2);
      }

      if (this.previousPosY) {
        if (this.mapElement.offsetHeight - this.height > this.previousPosY) {
          this.posY = this.previousPosY;
        } else {
          this.posY = 20;
        }
      } else {
        this.posY = Math.round(
          (this.mapElement.offsetHeight - this.height) / 2
        );
      }
      bus.$emit("update-object-form-position", { x: this.posX, y: this.posY });
    },

    /**
     * Change view mode of the object. 'View' <-> 'Edit'
     */
    changeViewModeOfObject(viewType) {
      this.$emit("changeViewMode", viewType);
    },

    onDragStop(x, y) {
      bus.$emit("update-object-form-position", { x: x, y: y });
    },

    disableOverflowIfSmall() {
      let tabContainer = this.$refs.tabContainer;
      let tabs = this.$refs.objectForm.querySelectorAll(".tab-content");
      let maxHeight = window.innerHeight * 0.65;
      for (const tab of tabs) {
        let tabHeight = tab.offsetHeight;
        if (tabHeight > 0) {
          if (tabHeight < maxHeight) {
            tabContainer.style.overflow = "visible";
          } else {
            tabContainer.style.overflow = "auto";
          }
        }
      }
    },

    updateContainerDimensions() {
      this.$nextTick(() => {
        if (this.$refs.objectForm && this.$refs.objectForm.clientHeight) {
          this.height = this.$refs.objectForm.clientHeight;
        }
      });
    },

    tryCloseForm() {
      if (this.dataWasChanged) {
        this.closeDialogIsOpen = true;
      } else {
        this.closeForm();
      }
    },

    closeForm() {
      this.$emit("close");
    },

    changeTab(index) {
      this.$emit("updateActiveTab", index);
    },

    hasPermissionToSeeTab(tab) {
      if (tab.hasOwnProperty("view_permission") && tab.view_permission) {
        if (tab.view_permission.name.includes("&")) {
          let permissions = tab.view_permission.name.split("&");
          return this.userHasFibercloudPermission(permissions[0]) && this.userHasFibercloudPermission(permissions[1]); 
        }
        return this.userHasFibercloudPermission(tab.view_permission.name);
      }
      return true;
    },

    onResizing(left, top, width, height) {
      this.height = height;
      this.width = width;
      this.wasResized = true;
      this.$emit("resize-form");
    }
  }
};
</script>

<style scoped>
.home-form-tabs-container {
  width: 100%;
  min-height: 100px;
  height: 100%;
  max-height: 65vh;
  overflow: auto;
}

.home-form-container .footer-button {
  font-size: 8px;
  height: 22px !important;
  min-width: 70px !important;
}

#save-button.disabled {
  cursor: default;
  background-color: rgba(0, 0, 0, 0.12) !important;
  color: #bebec1;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_Templates/DynamicObjectFormStructure.vue